/**
 * Atlas PM · 项目星图 · 任务形态多选与完成度（P8.10）
 *
 * 位置：星图顶部信息条（.star-head）下方、画布内容上方的空白带。
 *
 * 行为（用户确认口径）：
 *   - 可多选任务形态，仅【选中】的形态展示卡片，未选中的完全不显示；
 *   - 选择【不改变星图本身】——星座/节点/连线保持原样；
 *   - 多种形态并排成多张卡片；
 *   - 形态完成度 = 该形态下所有任务 progressPct 的平均值（保留 1 位小数，夹在 0~100）；
 *   - 取消全部选择 → 仅保留选择器本身，卡片区域整体消失（无空框、不占位）。
 *
 * ⭐ 零任务形态：直接从选择器候选中【省略】，绝不显示一张 0% 的假卡片。
 * ⭐ 选项来源：复用 P8.6/P8.9 的 `useTaskCategoryOptions()`（只含启用中字典项），
 *    颜色从同一 TanStack Query 缓存里取，不新增接口、不产生额外请求。
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { TaskCategoryItem } from '../settings/task-categories/api';
import { useTaskCategoryOptions } from '../task-form/useTaskCategoryOptions';
import type { StarTaskInput } from './layout';

export interface StarCategoryMetricsProps {
  /** 当前项目的星图任务（useStarData 拍平后的结果） */
  tasks: readonly StarTaskInput[];
  /** 切换项目时清空已选形态 */
  projectId?: string | null;
}

interface CategoryAggregate {
  id: string;
  name: string;
  color: string;
  taskCount: number;
  avgPct: number;
}

/** 字典颜色 → CSS 颜色；非法值回退青色 */
function normalizeColor(color?: string | null): string {
  if (color && /^#?[0-9a-fA-F]{6}$/.test(color)) {
    return color.startsWith('#') ? color : `#${color}`;
  }
  return '#5EE7FF';
}

/** 参与平均的进度：非数字按 0，夹在 0~100 */
function clampProgress(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

export default function StarCategoryMetrics({ tasks, projectId }: StarCategoryMetricsProps) {
  const { query, options } = useTaskCategoryOptions();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  /* 切换项目 → 清空选择并收起菜单 */
  useEffect(() => {
    setSelectedIds([]);
    setOpen(false);
  }, [projectId]);

  /* 点击外部收起菜单 */
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  /* 字典颜色：与 useTaskCategoryOptions 共用同一缓存，不额外请求 */
  const colorById = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const item of (query.data as TaskCategoryItem[] | undefined) ?? []) {
      map.set(item.id, item.color);
    }
    return map;
  }, [query.data]);

  /**
   * 按启用中的字典项聚合任务：
   *   - 优先按 categoryId 匹配；
   *   - 缺 categoryId 的历史任务回退按 categoryName 匹配；
   *   - 无任务的形态直接省略（不提供选项）。
   */
  const aggregates = useMemo<CategoryAggregate[]>(() => {
    const byId = new Map<string, StarTaskInput[]>();
    const byName = new Map<string, StarTaskInput[]>();
    for (const task of tasks) {
      if (task.categoryId) {
        const arr = byId.get(task.categoryId);
        if (arr) arr.push(task);
        else byId.set(task.categoryId, [task]);
      } else if (task.categoryName) {
        const arr = byName.get(task.categoryName);
        if (arr) arr.push(task);
        else byName.set(task.categoryName, [task]);
      }
    }

    const out: CategoryAggregate[] = [];
    for (const option of options) {
      const list = [...(byId.get(option.value) ?? []), ...(byName.get(option.label) ?? [])];
      if (list.length === 0) continue;

      let sum = 0;
      for (const task of list) sum += clampProgress(task.progressPct);
      const avgPct = Math.max(0, Math.min(100, sum / list.length));
      const fallbackColor = list.find((t) => t.categoryColor)?.categoryColor;

      out.push({
        id: option.value,
        name: option.label,
        color: normalizeColor(colorById.get(option.value) ?? fallbackColor),
        taskCount: list.length,
        avgPct,
      });
    }
    return out;
  }, [tasks, options, colorById]);

  const chosen = useMemo(
    () => aggregates.filter((a) => selectedIds.includes(a.id)),
    [aggregates, selectedIds],
  );

  /* 该项目没有任何「有任务的启用形态」→ 不占用星图空间 */
  if (aggregates.length === 0) return null;

  const toggle = (id: string): void => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  return (
    <div className="star-catmetrics" ref={rootRef}>
      <div className="star-catmetrics__picker" data-testid="star-category-filter">
        <button
          type="button"
          className={`star-catmetrics__trigger${open ? ' on' : ''}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span>任务形态</span>
          {chosen.length > 0 && (
            <span className="star-catmetrics__count">{chosen.length}</span>
          )}
          <span className="star-catmetrics__chev">{open ? '▴' : '▾'}</span>
        </button>

        {open && (
          <div className="star-catmetrics__menu" role="listbox" aria-multiselectable="true">
            {aggregates.map((item) => {
              const checked = selectedIds.includes(item.id);
              return (
                <label
                  key={item.id}
                  className={`star-catmetrics__opt${checked ? ' on' : ''}`}
                  role="option"
                  aria-selected={checked}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggle(item.id)} />
                  <i
                    className="star-catmetrics__dot"
                    style={{ background: item.color, color: item.color }}
                  />
                  <span className="star-catmetrics__opt-name">{item.name}</span>
                  <span className="star-catmetrics__opt-count">{item.taskCount}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {chosen.length > 0 && (
        <div className="star-catmetrics__cards">
          {chosen.map((item) => (
            <div className="star-catmetrics__card" data-testid="star-category-card" key={item.id}>
              <i
                className="star-catmetrics__dot"
                style={{ background: item.color, color: item.color }}
              />
              <div className="star-catmetrics__card-body">
                <div className="star-catmetrics__card-name">{item.name}</div>
                <div className="star-catmetrics__card-meta">{item.taskCount} 个任务</div>
              </div>
              <div className="star-catmetrics__card-value">{item.avgPct.toFixed(1)}%</div>
              <button
                type="button"
                className="star-catmetrics__remove"
                title="移除"
                aria-label={`移除 ${item.name}`}
                onClick={() => toggle(item.id)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
